import type { Recommendation } from '@/types';

import { SubscribeButton } from '../SubscribeButton';

interface RecommendationRowProps {
  email?: string;
  recommendation: Recommendation;
  submittedIds: string[];
  translations?: Record<string, string>;
  onSubmitSuccess?: (recommendations: Recommendation[]) => void;
  SubscribeForm: React.ComponentType<{
    recommendations: Recommendation[];
    triggerSource?: 'individual_card_button' | 'subscribe_all_button';
    onSubmitSuccess?: (recommendations: Recommendation[]) => void;
    children: (props: { isLoading: boolean; email?: string }) => React.ReactNode;
  }>;
}

export const RecommendationRow: React.FC<RecommendationRowProps> = ({
  recommendation,
  submittedIds,
  translations,
  onSubmitSuccess,
  SubscribeForm,
}) => {
  return (
    <div className="p-4 rounded-[var(--site-theme-border-radius,6px)] border border-[var(--site-theme-border-color,#e5e7eb)] bg-[var(--site-theme-background-color,#ffffff)] flex flex-col gap-2">
      <div className="flex items-center gap-3">
        <img
          src={recommendation.logo_url}
          alt={`${recommendation.name}-logo`}
          className="w-8 h-8 rounded grow-0 shrink-0 object-cover"
        />
        <div className="flex-1 min-w-0">
          <h3
            className="truncate text-sm font-semibold text-[var(--site-theme-text-on-background-color,#222222)]"
            style={{ fontFamily: 'var(--site-theme-header-font)' }}
          >
            {recommendation.name}
          </h3>
          {recommendation.publisher_name && (
            <p
              className="truncate text-xs font-normal text-[var(--site-theme-text-on-background-color,#222222)] opacity-60"
              style={{ fontFamily: 'var(--site-theme-body-font)' }}
            >
              {`${translations?.['recommendations.modal.by'] || 'by'} ${recommendation.publisher_name}`}
            </p>
          )}
        </div>
        <div className="shrink-0">
          <SubscribeForm
            recommendations={[recommendation]}
            triggerSource="individual_card_button"
            onSubmitSuccess={() => onSubmitSuccess?.([recommendation])}
          >
            {({ isLoading, email }) => (
              <SubscribeButton
                recommendation={recommendation}
                isLoading={isLoading}
                email={email}
                submittedIds={submittedIds}
                translations={translations}
                variant="solid"
              />
            )}
          </SubscribeForm>
        </div>
      </div>

      {recommendation.sponsored && (
        <p className="text-xs font-medium text-[var(--site-theme-text-on-background-color,#222222)] opacity-60">
          {translations?.['recommendations.modal.sponsored'] || 'Sponsored'}
        </p>
      )}

      {!!recommendation.badge && (
        <span className="shadow-xs inline-flex items-center text-xs font-medium rounded bg-indigo-100 text-indigo-900 border-indigo-200 border px-1.5 py-0.5 gap-x-1 w-max">
          <span className="w-full block overflow-hidden whitespace-nowrap text-ellipsis">{recommendation.badge}</span>
        </span>
      )}

      {recommendation.description && (
        <p
          className="text-xs font-normal text-[var(--site-theme-text-on-background-color,#222222)] opacity-60 line-clamp-2"
          style={{ fontFamily: 'var(--site-theme-body-font)' }}
        >
          {recommendation.description}
        </p>
      )}
    </div>
  );
};
